import React from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import Image from 'next/image';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { useIntl } from 'react-intl';
import {
  bgPrimary,
  bgLavender,
  bgOlive,
  bgGreen,
  bgOrange,
  bgTeal,
  bgPink,
  text0,
} from '@osmosis/tailwind-ui/styles';

interface Props {
  preferredName?: string;
  lastName?: string;
  avatar?: string;
  size?: number;
  onClick?: () => void;
  role?: string;
}

const bgColors = [
  `${bgPrimary}`,
  `${bgLavender}`,
  `${bgOlive}`,
  `${bgGreen}`,
  `${bgOrange}`,
  'bg-red-600',
  `${bgTeal}`,
  `${bgPink}`,
];

const getInitials = (first = '', last = ''): string => {
  if (!first && !last) return 'OU';

  return `${first[0]}${last[0]}`.toUpperCase();
};

/**
 * Avatar.
 */
const Avatar = (props: Props): JSX.Element => {
  const intl = useIntl();

  const {
    preferredName = '',
    lastName = '',
    avatar = '',
    size = 40,
    role = 'button',
    onClick,
  } = props;
  const userBg = preferredName?.charCodeAt(0) % bgColors.length || 0;

  return (
    <Button
      rounded={true}
      className={`flex w-[${size}px] h-[${size}px] p-0 ${
        onClick ? 'cursor-pointer' : 'cursor-default'
      } select-none ${
        bgColors[userBg]
      } items-center justify-center overflow-hidden hover:${
        bgColors[userBg]
      } active:${bgColors[userBg]}`}
      aria-label={intl.formatMessage({
        id: 'avatar.label',
        defaultMessage: 'Account information',
      })}
      role={role}
      disabled={role !== 'button'}
      data-testid={`user-avatar-${preferredName.trim() || 'Osmosis'}-${
        lastName.trim() || 'User'
      }`}
      data-tooltip-id={`avatar-tooltip-${preferredName.trim() || 'Osmosis'}-${
        lastName.trim() || 'User'
      }`}
      onClick={onClick}
    >
      {avatar ? (
        <Flex
          className={`relative w-[${size}px] h-[${size}px] `}
          data-testid="user-avatar"
        >
          <Image
            src={avatar}
            fill
            sizes="(max-width: 120px) 100vw"
            className="rounded-full object-cover"
            alt=""
            role="img"
            data-tooltip-id={`avatar-tooltip-${
              preferredName.trim() || 'Osmosis'
            }-${lastName || 'User'}`}
          />
        </Flex>
      ) : (
        <P
          data-testid="user-initials"
          className={`text-base font-bold ${text0} sm:text-lg`}
        >
          {getInitials(preferredName.trim(), lastName.trim())}
        </P>
      )}
      {onClick && (
        <Tooltip
          id={`avatar-tooltip-${preferredName.trim() || 'Osmosis'}-${
            lastName || 'User'
          }`}
          content={`${preferredName.trim() || 'Osmosis'} ${
            lastName.trim() || 'User'
          }`}
          place="bottom"
        />
      )}
    </Button>
  );
};

export default Avatar;
